import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  MessageSquare, Phone, Plus, Trash2, Send, Activity, AlertCircle, CheckCircle2, X,
} from "lucide-react";
import { useT } from "@/lib/i18n";

const KIND_LABEL = {
  login: { es: "Inicio de sesión", en: "Login" },
  course_completed: { es: "Curso completado", en: "Course completed" },
  points_milestone: { es: "Puntos · meta", en: "Points milestone" },
  test: { es: "Prueba", en: "Test" },
};

function getCurrentLocaleSimple() {
  try {
    return localStorage.getItem("app_locale") || "en";
  } catch {
    return "en";
  }
}

const STATUS_STYLES = {
  sent: "bg-[#1B2E7A]/10 text-[#1B2E7A] border-[#1B2E7A]/30",
  mocked: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
  error: "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/30",
  no_recipients: "bg-[#FEF3C7] text-[#B45309] border-[#F4B81F]/30",
  disabled: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
};

export default function AdminSMS() {
  const t = useT();
  const [config, setConfig] = useState({ recipients: [], enabled: true, twilio_ready: false, from_number: "", messaging_service_sid: "", sender_mode: "none" });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPhone, setNewPhone] = useState("");
  const [savingPhones, setSavingPhones] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testBody, setTestBody] = useState("Mensaje de prueba desde Mueblería Canales · plataforma de capacitación.");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, lg] = await Promise.all([
        api.get("/admin/sms/config"),
        api.get("/admin/sms/logs?limit=100"),
      ]);
      setConfig(cfg.data);
      setLogs(lg.data.logs || []);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === logs.length) return new Set();
      return new Set(logs.map((l) => l.id));
    });
  };

  const deleteOne = async (id) => {
    if (!window.confirm(t("sms.confirm_delete_one"))) return;
    try {
      await api.delete(`/admin/sms/logs/${id}`);
      toast.success(t("sms.deleted_one"));
      setLogs((prev) => prev.filter((l) => l.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t("sms.confirm_delete_bulk", { n: selectedIds.size }))) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await api.post("/admin/sms/logs/bulk-delete", { ids });
      toast.success(t("sms.deleted_bulk", { n: r.data.deleted }));
      setLogs((prev) => prev.filter((l) => !selectedIds.has(l.id)));
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setBulkBusy(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(t("sms.confirm_clear_all"))) return;
    setBulkBusy(true);
    try {
      const r = await api.delete("/admin/sms/logs?confirm=yes");
      toast.success(t("sms.deleted_bulk", { n: r.data.deleted }));
      setLogs([]);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addPhone = async () => {
    const trimmed = newPhone.trim();
    if (!trimmed) return;
    setSavingPhones(true);
    try {
      const next = [...config.recipients, trimmed];
      const r = await api.patch("/admin/sms/config", { recipients: next });
      setConfig({ ...config, recipients: r.data.recipients });
      setNewPhone("");
      toast.success(t("sms.phone_added"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSavingPhones(false);
    }
  };

  const removePhone = async (idx) => {
    if (!window.confirm(t("sms.confirm_remove"))) return;
    setSavingPhones(true);
    try {
      const next = config.recipients.filter((_, i) => i !== idx);
      const r = await api.patch("/admin/sms/config", { recipients: next });
      setConfig({ ...config, recipients: r.data.recipients });
      toast.success(t("sms.phone_removed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSavingPhones(false);
    }
  };

  const toggleEnabled = async (val) => {
    try {
      const r = await api.patch("/admin/sms/config", { enabled: val });
      setConfig({ ...config, enabled: r.data.enabled });
      toast.success(val ? t("sms.enabled") : t("sms.disabled"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      await api.post("/admin/sms/test", { body: testBody });
      toast.success(t("sms.test_sent"));
      setTimeout(load, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setTestBusy(false);
    }
  };

  const sentCount = logs.filter((l) => l.status === "sent").length;
  const mockedCount = logs.filter((l) => l.status === "mocked").length;
  const errorCount = logs.filter((l) => l.status === "error").length;

  return (
    <div data-testid="admin-sms-page" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] inline-flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5" /> {t("sms.eyebrow")}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] mb-3">{t("sms.title")}</h1>
        <p className="text-[#6B7280] max-w-2xl">{t("sms.subtitle")}</p>
      </div>

      {/* Status banner */}
      <div
        className={`rounded-2xl p-5 border-2 ${
          config.twilio_ready ? "bg-[#1B2E7A]/5 border-[#1B2E7A]/30" : "bg-[#FEF3C7] border-[#F4B81F]/40"
        }`}
        data-testid="sms-status-banner"
      >
        <div className="flex items-start gap-3">
          {config.twilio_ready ? (
            <CheckCircle2 className="h-6 w-6 text-[#1B2E7A] mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="h-6 w-6 text-[#B45309] mt-0.5 shrink-0" />
          )}
          <div className="flex-1">
            <p className="font-medium text-[#1A1A1A]">
              {config.twilio_ready ? t("sms.status_active") : t("sms.status_mock")}
            </p>
            <p className="text-sm text-[#6B7280] mt-1">
              {config.twilio_ready ? t("sms.status_active_desc") : t("sms.status_mock_desc")}
            </p>
            {!config.twilio_ready && (
              <code className="block mt-3 text-xs bg-white/60 p-2 rounded border border-[#E5E7EB] font-mono text-[#374151]">
                TWILIO_ACCOUNT_SID="AC..."<br/>
                TWILIO_AUTH_TOKEN="..."<br/>
                TWILIO_FROM_NUMBER="+1..."
              </code>
            )}
            {/* Sender mode badge */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">Sender:</span>
              {config.sender_mode === "messaging_service" && (
                <span className="text-xs px-2 py-1 rounded bg-[#1B2E7A]/10 text-[#1B2E7A] font-mono" data-testid="sms-sender-mode">
                  MessagingService · {config.messaging_service_sid ? config.messaging_service_sid.slice(0, 12) + '…' : ''}
                </span>
              )}
              {config.sender_mode === "phone_number" && (
                <span className="text-xs px-2 py-1 rounded bg-[#1B2E7A]/10 text-[#1B2E7A] font-mono" data-testid="sms-sender-mode">
                  From · {config.from_number}
                </span>
              )}
              {config.sender_mode === "none" && (
                <span className="text-xs px-2 py-1 rounded bg-[#FEF3C7] text-[#B45309] border border-[#F4B81F]/40" data-testid="sms-sender-mode">
                  Sin remitente configurado
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B7280]">{config.enabled ? t("sms.on") : t("sms.off")}</span>
            <Switch
              checked={config.enabled}
              onCheckedChange={toggleEnabled}
              data-testid="sms-toggle"
            />
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="sms-kpi-sent">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{t("sms.kpi_sent")}</p>
          <p className="font-heading text-3xl text-[#1B2E7A] mt-1">{sentCount}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="sms-kpi-mocked">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{t("sms.kpi_mocked")}</p>
          <p className="font-heading text-3xl text-[#6B7280] mt-1">{mockedCount}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="sms-kpi-error">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{t("sms.kpi_error")}</p>
          <p className="font-heading text-3xl text-[#DC2626] mt-1">{errorCount}</p>
        </div>
      </div>

      {/* Recipients */}
      <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">{t("sms.recipients_label")}</p>
        <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">{t("sms.recipients_title")}</h2>
        <p className="text-sm text-[#6B7280] mb-4">{t("sms.recipients_desc")}</p>

        <div className="space-y-2 mb-4">
          {config.recipients.length === 0 ? (
            <p className="text-sm text-[#6B7280] italic">{t("sms.no_recipients")}</p>
          ) : (
            config.recipients.map((r, i) => (
              <div key={r} className="flex items-center gap-3 bg-[#FFFFFF] rounded-lg px-3 py-2" data-testid={`sms-recipient-${i}`}>
                <Phone className="h-4 w-4 text-[#6B7280]" />
                <span className="font-mono text-sm text-[#1A1A1A] flex-1">{r}</span>
                <button
                  onClick={() => removePhone(i)}
                  disabled={savingPhones}
                  className="text-[#DC2626] hover:text-[#A05541] p-1 rounded hover:bg-white"
                  data-testid={`sms-remove-${i}`}
                  aria-label={t("sms.remove")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="+1XXXXXXXXXX"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addPhone()}
            className="flex-1 font-mono"
            data-testid="sms-new-phone"
          />
          <Button
            onClick={addPhone}
            disabled={!newPhone.trim() || savingPhones}
            className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
            data-testid="sms-add-phone"
          >
            <Plus className="h-4 w-4 mr-1" /> {t("sms.add")}
          </Button>
        </div>
        <p className="text-[10px] text-[#6B7280] mt-2">
          {t("sms.format_hint")}
        </p>
      </section>

      {/* Test */}
      <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">{t("sms.test_label")}</p>
        <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">{t("sms.test_title")}</h2>
        <Input
          value={testBody}
          onChange={(e) => setTestBody(e.target.value)}
          maxLength={140}
          data-testid="sms-test-body"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-[#6B7280]">{testBody.length}/140</span>
          <Button
            onClick={sendTest}
            disabled={testBusy || !testBody.trim()}
            className="bg-[#F4B81F] hover:bg-[#D49538] text-[#1A1A1A]"
            data-testid="sms-send-test"
          >
            <Send className="h-4 w-4 mr-1" /> {testBusy ? t("common.loading") : t("sms.send_test")}
          </Button>
        </div>
      </section>

      {/* Logs */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] inline-flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5" /> {t("sms.history_label")}
          </p>
          <div className="flex items-center gap-3">
            {selectedIds.size > 0 && (
              <>
                <span className="text-xs text-[#6B7280]" data-testid="sms-selected-count">
                  {t("sms.n_selected", { n: selectedIds.size })}
                </span>
                <button
                  onClick={deleteSelected}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1 text-xs text-white bg-[#DC2626] hover:bg-[#A05541] px-3 py-1.5 rounded-md disabled:opacity-50"
                  data-testid="sms-delete-selected"
                >
                  <Trash2 className="h-3 w-3" /> {t("sms.delete_selected")}
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-[#6B7280] hover:text-[#1A1A1A]"
                  data-testid="sms-clear-selection"
                >
                  <X className="h-3 w-3 inline" /> {t("sms.clear_selection")}
                </button>
              </>
            )}
            {logs.length > 0 && selectedIds.size === 0 && (
              <button
                onClick={clearAll}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1 text-xs text-[#DC2626] hover:text-[#A05541] px-2 py-1"
                data-testid="sms-clear-all"
              >
                <Trash2 className="h-3 w-3" /> {t("sms.clear_all")}
              </button>
            )}
            <button onClick={load} className="text-xs text-[#1B2E7A] hover:underline" data-testid="sms-refresh">
              {t("common.refresh")}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-[#374151]">{t("common.loading")}</p>
        ) : logs.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 text-center">
            <MessageSquare className="h-10 w-10 text-[#6B7280] mx-auto mb-2" />
            <p className="text-[#6B7280]">{t("sms.history_empty")}</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FFFFFF] border-b border-[#E5E7EB]">
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === logs.length && logs.length > 0}
                      onChange={toggleSelectAll}
                      aria-label={t("sms.select_all")}
                      data-testid="sms-select-all"
                      className="accent-[#1B2E7A]"
                    />
                  </th>
                  <th className="px-4 py-3">{t("sms.col_time")}</th>
                  <th className="px-4 py-3">{t("sms.col_kind")}</th>
                  <th className="px-4 py-3">{t("sms.col_to")}</th>
                  <th className="px-4 py-3">{t("sms.col_status")}</th>
                  <th className="px-4 py-3">{t("sms.col_body")}</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr
                    key={l.id}
                    className={`border-t border-[#E5E7EB] ${selectedIds.has(l.id) ? "bg-[#F3F4F6]/60" : "hover:bg-[#FFFFFF]/50"}`}
                    data-testid={`sms-log-${l.id}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(l.id)}
                        onChange={() => toggleSelect(l.id)}
                        aria-label={t("sms.select_row")}
                        data-testid={`sms-select-${l.id}`}
                        className="accent-[#1B2E7A]"
                      />
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-[#1A1A1A]">
                      {(KIND_LABEL[l.kind] && KIND_LABEL[l.kind][getCurrentLocaleSimple()]) || (KIND_LABEL[l.kind] && KIND_LABEL[l.kind].en) || l.kind}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#374151]">{l.to || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded border ${STATUS_STYLES[l.status] || ""}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#374151] max-w-md truncate" title={l.body}>
                      {l.body}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteOne(l.id)}
                        className="text-[#DC2626] hover:text-[#A05541] p-1 rounded hover:bg-[#FBE8E2]"
                        aria-label={t("sms.delete_row")}
                        data-testid={`sms-delete-${l.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
